"use strict";
// This file contains utility functions for the API. Unfortunately we can't use StakeConnection directly because it has wasm imports that are not compatible with the Next API.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getLockSummary = exports.getCurrentlyLockedAmount = exports.getAllLockedCustodyAccounts = exports.getAllCustodyAccounts = exports.getAllMetadataAccounts = exports.getTotalSupply = exports.getConfig = exports.getStakeAccountDetails = exports.getStakeAccountsByOwner = exports.ONE_YEAR = void 0;
const bytes_1 = require("@coral-xyz/anchor/dist/cjs/utils/bytes");
const web3_js_1 = require("@solana/web3.js");
const bn_js_1 = __importDefault(require("bn.js"));
const constants_1 = require("./constants");
const lockedAccounts_1 = require("./lockedAccounts");
const pythBalance_1 = require("./pythBalance");
exports.ONE_YEAR = new bn_js_1.default(3600 * 24 * 365);
// ======================================
// PDA derivations
// ======================================
function getMetadataAccountAddress(positionAccountAddress) {
    return web3_js_1.PublicKey.findProgramAddressSync([Buffer.from("stake_metadata"), positionAccountAddress.toBuffer()], constants_1.STAKING_ADDRESS)[0];
}
function getCustodyAccountAddress(positionAccountAddress) {
    return web3_js_1.PublicKey.findProgramAddressSync([Buffer.from("custody"), positionAccountAddress.toBuffer()], constants_1.STAKING_ADDRESS)[0];
}
// ======================================
// One-user getters
// ======================================
function getStakeAccountsByOwner(connection, owner) {
    return __awaiter(this, void 0, void 0, function* () {
        const response = yield connection.getProgramAccounts(constants_1.STAKING_ADDRESS, {
            encoding: "base64",
            filters: [
                {
                    memcmp: {
                        offset: 0,
                        bytes: bytes_1.bs58.encode(Buffer.from("55c3f14f7cc04f0b", "hex")), // Positions account discriminator
                    },
                },
                {
                    memcmp: {
                        offset: 8,
                        bytes: owner.toBase58(),
                    },
                },
            ],
        });
        return response.map((account) => {
            return account.pubkey;
        });
    });
}
exports.getStakeAccountsByOwner = getStakeAccountsByOwner;
function getStakeAccountDetails(stakingProgram, tokenProgram, positionAccountAddress) {
    return __awaiter(this, void 0, void 0, function* () {
        const configAccountData = yield getConfig(stakingProgram);
        const metadataAccountAddress = getMetadataAccountAddress(positionAccountAddress);
        const metadataAccountData = yield stakingProgram.account.stakeAccountMetadataV2.fetch(metadataAccountAddress);
        const lock = metadataAccountData.lock;
        const custodyAccountAddress = getCustodyAccountAddress(positionAccountAddress);
        const custodyAccountData = yield tokenProgram.account.account.fetch(custodyAccountAddress);
        return {
            custodyAccount: custodyAccountAddress.toBase58(),
            actualAmount: new pythBalance_1.PythBalance(custodyAccountData.amount).toString(),
            lock: getLockSummary(lock, configAccountData.pythTokenListTime),
        };
    });
}
exports.getStakeAccountDetails = getStakeAccountDetails;
// ======================================
// Global getters
// ======================================
function getConfig(stakingProgram) {
    return __awaiter(this, void 0, void 0, function* () {
        const configAccountAddress = web3_js_1.PublicKey.findProgramAddressSync([Buffer.from("config")], constants_1.STAKING_ADDRESS)[0];
        return yield stakingProgram.account.globalConfig.fetch(configAccountAddress);
    });
}
exports.getConfig = getConfig;
function getTotalSupply(tokenProgram) {
    return __awaiter(this, void 0, void 0, function* () {
        const pythTokenMintData = yield tokenProgram.account.mint.fetch(constants_1.PYTH_TOKEN);
        return new pythBalance_1.PythBalance(pythTokenMintData.supply);
    });
}
exports.getTotalSupply = getTotalSupply;
function getAllMetadataAccounts(stakingProgram, stakeAccounts) {
    return __awaiter(this, void 0, void 0, function* () {
        const metadataAccountAddresses = stakeAccounts.map((account) => getMetadataAccountAddress(account));
        // split metadata accounts into chunks of 1000 to avoid hitting the limit
        const chunkSize = 1000;
        const chunks = Array.from({ length: Math.ceil(metadataAccountAddresses.length / chunkSize) }, (_, i) => metadataAccountAddresses.slice(i * chunkSize, i * chunkSize + chunkSize));
        // for each chunk, fetch the metadata accounts
        let allMetadataAccounts = [];
        for (let i = 0; i < chunks.length; i++) {
            const metadataAccounts = yield stakingProgram.account.stakeAccountMetadataV2.fetchMultiple(chunks[i]);
            allMetadataAccounts = allMetadataAccounts.concat(metadataAccounts);
        }
        return allMetadataAccounts;
    });
}
exports.getAllMetadataAccounts = getAllMetadataAccounts;
function getAllCustodyAccounts(tokenProgram, stakeAccounts) {
    return __awaiter(this, void 0, void 0, function* () {
        const allCustodyAccountAddresses = stakeAccounts.map((account) => getCustodyAccountAddress(account));
        return tokenProgram.account.account.fetchMultiple(allCustodyAccountAddresses);
    });
}
exports.getAllCustodyAccounts = getAllCustodyAccounts;
// ======================================
// Locked accounts
// ======================================
function getAllLockedStakeAccounts() {
    return lockedAccounts_1.LOCKED_ACCOUNTS_PERIODIC_AFTER_LISTING.map((account) => new web3_js_1.PublicKey(account));
}
/**
 * WARNING: This function uses a hardcoded list of locked staking accounts. This is because
 * otherwise you'd need to fetch all staking account and that's cumbersome since there are so many. On the other hand,
 * the list of locked staking accounts is small and doesn't change often.
 */
function getAllLockedCustodyAccounts(stakingProgram, tokenProgram) {
    return __awaiter(this, void 0, void 0, function* () {
        const configAccountData = yield getConfig(stakingProgram);
        const allLockedStakeAccounts = getAllLockedStakeAccounts();
        const allLockedMetadataAccounts = yield getAllMetadataAccounts(stakingProgram, allLockedStakeAccounts);
        const allLockedCustodyAccountAddresses = allLockedStakeAccounts.map((account) => getCustodyAccountAddress(account));
        const allLockedCustodyAccounts = yield tokenProgram.account.account.fetchMultiple(allLockedCustodyAccountAddresses);
        return allLockedCustodyAccounts
            .map((data, index) => {
            var _a;
            const amount = data.amount && ((_a = allLockedMetadataAccounts[index]) === null || _a === void 0 ? void 0 : _a.lock)
                ? new pythBalance_1.PythBalance(data.amount).min(getCurrentlyLockedAmount(allLockedMetadataAccounts[index].lock, configAccountData))
                : new pythBalance_1.PythBalance(new bn_js_1.default(0));
            return { pubkey: allLockedCustodyAccountAddresses[index], amount };
        })
            .sort((a, b) => {
            return a.amount.lte(b.amount) ? 1 : -1;
        }); // ! is safe because of the filter above
    });
}
exports.getAllLockedCustodyAccounts = getAllLockedCustodyAccounts;
function getCurrentlyLockedAmount(lock, configAccountData) {
    const listTime = configAccountData.pythTokenListTime;
    if (lock.fullyVested) {
        return pythBalance_1.PythBalance.zero();
    }
    else if (lock.periodicVestingAfterListing) {
        if (!listTime) {
            return new pythBalance_1.PythBalance(lock.periodicVestingAfterListing.initialBalance);
        }
        else {
            return getCurrentlyLockedAmountPeriodic(listTime, lock.periodicVestingAfterListing.periodDuration, lock.periodicVestingAfterListing.numPeriods, lock.periodicVestingAfterListing.initialBalance);
        }
    }
    else if (lock.periodicVesting) {
        return getCurrentlyLockedAmountPeriodic(lock.periodicVesting.startDate, lock.periodicVesting.periodDuration, lock.periodicVesting.numPeriods, lock.periodicVesting.initialBalance);
    }
    else {
        throw new Error("Should be unreachable");
    }
}
exports.getCurrentlyLockedAmount = getCurrentlyLockedAmount;
function getCurrentlyLockedAmountPeriodic(startDate, periodDuration, numPeriods, initialBalance) {
    const currentTimestamp = new bn_js_1.default(Math.floor(Date.now() / 1000));
    if (currentTimestamp.lte(startDate)) {
        return new pythBalance_1.PythBalance(initialBalance);
    }
    else {
        const periodsElapsed = currentTimestamp.sub(startDate).div(periodDuration);
        if (periodsElapsed.gte(numPeriods)) {
            return pythBalance_1.PythBalance.zero();
        }
        else {
            const remainingPeriods = numPeriods.sub(periodsElapsed);
            return new pythBalance_1.PythBalance(remainingPeriods.mul(initialBalance).div(numPeriods));
        }
    }
}
function getLockSummary(lock, listTime) {
    if (lock.fullyVested) {
        return { type: "fullyUnlocked" };
    }
    else if (lock.periodicVestingAfterListing) {
        return {
            type: "periodicUnlockingAfterListing",
            schedule: getUnlockEvents(listTime, lock.periodicVestingAfterListing.periodDuration, lock.periodicVestingAfterListing.numPeriods, lock.periodicVestingAfterListing.initialBalance),
        };
    }
    else if (lock.periodicVesting) {
        return {
            type: "periodicUnlocking",
            schedule: getUnlockEvents(lock.periodicVesting.startDate, lock.periodicVesting.periodDuration, lock.periodicVesting.numPeriods, lock.periodicVesting.initialBalance),
        };
    }
}
exports.getLockSummary = getLockSummary;
function getUnlockEvents(startData, periodDuration, numberOfPeriods, initialBalance) {
    if (startData) {
        return Array(numberOfPeriods.toNumber())
            .fill(0)
            .map((_, i) => {
            return {
                date: startData.add(periodDuration.muln(i + 1)).toString(),
                amount: new pythBalance_1.PythBalance(initialBalance.divn(numberOfPeriods.toNumber())).toString(),
            };
        });
    }
    return [];
}
